import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {
    CertificateApi,
    CertificateStore,
    MissingKeyAction,
    X509CertificateDetails
} from "components/src/api/admin/generated";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {useRef, useState} from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from "react-bootstrap/Spinner";
import {useNavigate} from "react-router-dom";

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface PrivateKeyImportDialogProps {
    certificateStore : CertificateStore
    onClose : OnClose
}

/*
 * REST api for accessing the back-end
 */
const certificateApi = new CertificateApi(ApiConfig.config());

export const PrivateKeyImportDialog = ({certificateStore, onClose} : PrivateKeyImportDialogProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to the settings of the
     * user if the email address is clicked
     */
    const navigate = useNavigate();
    /**
     * The files to import
     */
    const [file, setFile] = useState<File>();
    /**
     * The MissingKeyAction (SkipCertificateOnlyEntry, AddCertificateOnlyEntry)
     */
    const [missingKeyAction, setMissingKeyAction] = useState<MissingKeyAction>(MissingKeyAction.AddCertificateOnlyEntry);
    /**
     * The import key store password
     */
    const [keyStorePassword, setKeyStorePassword] = useState<string>();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Set to true while importing to show a spinner on the import button
     */
    const [importing, setImporting] = useState<boolean>(false);
    /**
     * Reference to the file input element
     */
    const fileInputRef = useRef<HTMLInputElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    const importPrivateKeys = () =>
    {
        setImporting(true);

        certificateApi.certificateControllerImportPrivateKeys(certificateStore, missingKeyAction, keyStorePassword, file)
            .then((response ) =>
            {
                const imported : X509CertificateDetails[] = response.data;

                if (imported.length > 0) {
                    onClose(DialogCloseCause.Succes);
                }
                else {
                    setErrorMessage(t("certificates.no-private-keys-were-imported"));
                }
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error importing private keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setImporting(false);
            });
    }

    // called when the dialog is closed without clicking import
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    return (
        <Modal show={true}
               onHide={() => onHideDialog()}
               onEntered={() => fileInputRef.current?.focus()}
               backdrop="static"
        >
            <Modal.Header closeButton>
                <Modal.Title>{t("certificates.import-private-keys", {certificatestore: certificateStore})}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>

                <Form.Group className="mb-3">
                    <Form.Label>{t("certificates.private-key-file")}</Form.Label>
                    <Form.Control
                        size="sm"
                        type="file"
                        accept=""
                        ref={fileInputRef}
                        onChange={e => setFile(((e.target as HTMLInputElement).files as FileList)[0])}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>{t("certificates.private-key-password")}</Form.Label>
                    <Form.Control type="password"
                                  placeholder={t("certificates.private-key-password")}
                                  onChange={e => setKeyStorePassword(e.target.value)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>{t("certificates.import-action")}</Form.Label>
                    <Form.Select value={missingKeyAction}
                                 onChange={(e) => setMissingKeyAction(Utils.stringToEnum(MissingKeyAction, e.target.value as MissingKeyAction))}>
                        <option value="SKIP_CERTIFICATE_ONLY_ENTRY">{t("certificates.skip-certificate-only")}</option>
                        <option value="ADD_CERTIFICATE_ONLY_ENTRY">{t("certificates.add-certificate-only")}</option>
                    </Form.Select>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={importPrivateKeys}
                        disabled={file === undefined}>
                    {importing && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {importing ? t("common.importing") : t("common.import")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
